import { hasSameValues } from '@ratehub/base-ui';

import getSumOfObjectValues from './getSumOfObjectValues';
import getMonthlySpendingFromAverage from './getMonthlySpendingFromAverage';


/** @typedef {import('@ratehub/documents/cc-journey-document/stable').MonthlySpendingCategories} MonthlySpendingCategories */
/**
 * @typedef {object} MonthlySpendingSummary
 * @property {number} monthlySpendingAverage - sum of the individual spending categories (as a number)
 * @property {MonthlySpendingCategories} monthlySpending - categorized expenses
 */

/**
 * Gets an object containing monthlySpending and monthlySpendingAverage values based on the previous monthlySpending
 * ratio to monthlySpendingAverage or using a default ratio to monthlySpendingAverage.
 * @param {MonthlySpendingSummary} current - The current monthlySpending and monthlySpendingAverage.
 * @param {MonthlySpendingSummary} previous - The previous monthlySpending and monthlySpendingAverage.
 * @returns {MonthlySpendingSummary} The updated monthlySpending and monthlySpendingAverage.
 */
function getMonthlySpending(
    { monthlySpending, monthlySpendingAverage },
    { monthlySpending: previousMonthlySpending, monthlySpendingAverage: previousMonthlySpendingAverage },
) {
    // Create local variables to prevent mutating values
    let newMonthlySpending = { ...monthlySpending };
    let newMonthlySpendingAverage = monthlySpendingAverage;

    // Check if the new monthly spending values are different from the current values
    if (
        monthlySpendingAverage !== previousMonthlySpendingAverage
        || monthlySpending == null
    ) {
        // If the current monthlySpending is undefined/null or
        // if the previous and current monthlySpendingAverage are different
        // we want to update the monthlySpending based on a ratio calculation
        newMonthlySpending = getMonthlySpendingFromAverage(
            monthlySpendingAverage,
            newMonthlySpending,
        );
    } else if (!hasSameValues(newMonthlySpending, previousMonthlySpending)) {
        // If the new monthlySpending are different, calculate the new monthlySpendingAverage
        newMonthlySpendingAverage = getSumOfObjectValues(newMonthlySpending);
    }

    return {
        monthlySpendingAverage: newMonthlySpendingAverage,
        monthlySpending: newMonthlySpending,
    };
}

export default getMonthlySpending;
